import { supabase } from "@/integrations/supabase/client";
import {
  CollaboratorPromotion,
  CollaboratorEarning,
  CollaboratorPayout,
  CollaboratorStats,
} from "@/types/collaborator";

/**
 * Fetch promotions that a user is a collaborator for
 */
export const fetchUserCollaboratorPromotions = async (
  userId: number
): Promise<CollaboratorPromotion[]> => {
  const { data, error } = await supabase
    .from("promotions")
    .select("*")
    .eq("collaborator_id", userId);

  if (error) {
    console.error("Error fetching collaborator promotions:", error);
    throw error;
  }

  return data || [];
};

/**
 * Fetch all collaborator earnings (for listings)
 */
export const fetchAllCollaboratorEarnings = async (): Promise<CollaboratorEarning[]> => {
  const { data, error } = await supabase
    .from("collaborator_earnings")
    .select(
      `
      *,
      promotions (
        id,
        name,
        code
      ),
      user_subscriptions (
        id,
        price,
        currency_code,
        user_id,
        subscription_plan_id,
        status,
        subscription_start
      )
    `
    )
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching all collaborator earnings:", error);
    throw error;
  }

  return data || [];
};

/**
 * Fetch all collaborator payouts (for listings)
 */
export const fetchAllCollaboratorPayouts = async (): Promise<CollaboratorPayout[]> => {
  const { data, error } = await supabase
    .from("collaborator_payouts")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching all collaborator payouts:", error);
    throw error;
  }

  return data || [];
};

/**
 * Fetch earnings for a collaborator
 */
export const fetchUserCollaboratorEarnings = async (
  userId: number
): Promise<CollaboratorEarning[]> => {
  const { data, error } = await supabase
    .from("collaborator_earnings")
    .select(
      `
      *,
      promotions (
        id,
        name,
        code
      ),
      user_subscriptions (
        id,
        price,
        currency_code
      )
    `
    )
    .eq("collaborator_id", userId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching collaborator earnings:", error);
    throw error;
  }

  return data || [];
};

/**
 * Fetch payouts for a collaborator
 */
export const fetchUserCollaboratorPayouts = async (
  userId: number
): Promise<CollaboratorPayout[]> => {
  const { data, error } = await supabase
    .from("collaborator_payouts")
    .select("*")
    .eq("collaborator_id", userId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching collaborator payouts:", error);
    throw error;
  }

  return data || [];
};

/**
 * Fetch collaborator earnings overview (for dashboard cards)
 */
export const fetchCollaboratorEarningsOverview = async (): Promise<{
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
  totalTransactions: number;
}> => {
  try {
    // Get all earnings data for calculations
    const { data: allEarnings, error: earningsError } = await supabase
      .from("collaborator_earnings")
      .select("amount_earned, payout_id");

    if (earningsError) throw earningsError;

    // Get all payouts to determine status
    const { data: allPayouts, error: payoutsError } = await supabase
      .from("collaborator_payouts")
      .select("id, status");

    if (payoutsError) throw payoutsError;

    // Create payout lookup
    const payoutMap = Object.fromEntries(
      (allPayouts || []).map(p => [p.id, p.status])
    );

    const totalEarnings = (allEarnings || []).reduce(
      (sum, e) => sum + (e.amount_earned || 0),
      0
    );
    const pendingEarnings = (allEarnings || [])
      .filter(e => !e.payout_id)
      .reduce((sum, e) => sum + (e.amount_earned || 0), 0);
    const paidEarnings = (allEarnings || [])
      .filter(e => e.payout_id && payoutMap[e.payout_id] === "completed")
      .reduce((sum, e) => sum + (e.amount_earned || 0), 0);

    return {
      totalEarnings,
      pendingEarnings,
      paidEarnings,
      totalTransactions: (allEarnings || []).length,
    };
  } catch (error) {
    console.error("Error fetching collaborator earnings overview:", error);
    throw error;
  }
};

/**
 * Fetch collaborator payouts overview (for dashboard cards)
 */
export const fetchCollaboratorPayoutsOverview = async (): Promise<{
  totalPayouts: number;
  pendingPayouts: number;
  completedPayouts: number;
  failedPayouts: number;
  totalAmount: number;
  pendingAmount: number;
}> => {
  try {
    // Get all payouts data for calculations
    const { data: allPayouts, error } = await supabase
      .from("collaborator_payouts")
      .select("amount, status");

    if (error) throw error;

    const totalPayouts = (allPayouts || []).length;
    const pendingPayouts = (allPayouts || []).filter(p => p.status === "pending").length;
    const completedPayouts = (allPayouts || []).filter(p => p.status === "completed").length;
    const failedPayouts = (allPayouts || []).filter(p => p.status === "failed").length;
    const totalAmount = (allPayouts || []).reduce((sum, p) => sum + (p.amount || 0), 0);
    const pendingAmount = (allPayouts || [])
      .filter(p => p.status === "pending")
      .reduce((sum, p) => sum + (p.amount || 0), 0);

    return {
      totalPayouts,
      pendingPayouts,
      completedPayouts,
      failedPayouts,
      totalAmount,
      pendingAmount,
    };
  } catch (error) {
    console.error("Error fetching collaborator payouts overview:", error);
    throw error;
  }
};

/**
 * Fetch collaborators overview (for dashboard cards)
 */
export const fetchCollaboratorsOverview = async (): Promise<{
  totalCollaborators: number;
  totalEarnings: number;
  totalPendingEarnings: number;
  totalActivePromotions: number;
}> => {
  try {
    // Get all collaborators (users with promotions)
    const { data: promotionsData, error: promotionsError } = await supabase
      .from("promotions")
      .select("collaborator_id, is_active")
      .not("collaborator_id", "is", null);

    if (promotionsError) throw promotionsError;

    const collaboratorIds = [...new Set((promotionsData || []).map(p => p.collaborator_id))];
    const totalCollaborators = collaboratorIds.length;
    const totalActivePromotions = (promotionsData || []).filter(p => p.is_active).length;

    if (collaboratorIds.length === 0) {
      return {
        totalCollaborators: 0,
        totalEarnings: 0,
        totalPendingEarnings: 0,
        totalActivePromotions: 0,
      };
    }

    // Get earnings for all collaborators
    const { data: earningsData, error: earningsError } = await supabase
      .from("collaborator_earnings")
      .select("amount_earned, payout_id")
      .in("collaborator_id", collaboratorIds);

    if (earningsError) throw earningsError;

    const totalEarnings = (earningsData || []).reduce(
      (sum, e) => sum + (e.amount_earned || 0),
      0
    );
    const totalPendingEarnings = (earningsData || [])
      .filter(e => !e.payout_id)
      .reduce((sum, e) => sum + (e.amount_earned || 0), 0);

    return {
      totalCollaborators,
      totalEarnings,
      totalPendingEarnings,
      totalActivePromotions,
    };
  } catch (error) {
    console.error("Error fetching collaborators overview:", error);
    throw error;
  }
};

/**
 * Calculate statistics for a collaborator
 */
export const fetchUserCollaboratorStats = async (
  userId: number
): Promise<CollaboratorStats> => {
  try {
    // Fetch all the data we need to calculate stats
    const [promotions, earnings, payouts] = await Promise.all([
      fetchUserCollaboratorPromotions(userId),
      fetchUserCollaboratorEarnings(userId),
      fetchUserCollaboratorPayouts(userId),
    ]);

    // Calculate statistics
    const stats: CollaboratorStats = {
      totalEarnings: earnings.reduce(
        (sum, item) => sum + (item.amount_earned || 0),
        0
      ),
      pendingEarnings: earnings
        .filter((item) => !item.payout_id)
        .reduce((sum, item) => sum + (item.amount_earned || 0), 0),
      totalPayouts: payouts.reduce((sum, item) => sum + (item.amount || 0), 0),
      pendingPayouts: payouts
        .filter((item) => item.status === "pending")
        .reduce((sum, item) => sum + (item.amount || 0), 0),
      activePromotions: promotions.filter((item) => item.is_active).length,
      totalPromotions: promotions.length,
      totalSubscriptions: earnings.length,
    };

    return stats;
  } catch (error) {
    console.error("Error calculating collaborator stats:", error);
    throw error;
  }
};

/**
 * Create a new payout request by aggregating all pending earnings
 * This function ensures atomic operations to avoid partial updates
 */
export const createPayoutRequest = async (
  collaboratorId: number
): Promise<{ payout: CollaboratorPayout; earningsCount: number }> => {
  try {
    // Start a transaction-like operation by first fetching all pending earnings
    const { data: pendingEarnings, error: earningsError } = await supabase
      .from("collaborator_earnings")
      .select("id, amount_earned")
      .eq("collaborator_id", collaboratorId)
      .is("payout_id", null);

    if (earningsError) {
      console.error("Error fetching pending earnings:", earningsError);
      throw earningsError;
    }

    if (!pendingEarnings || pendingEarnings.length === 0) {
      throw new Error("No pending earnings found for this collaborator");
    }

    // Calculate total amount
    const totalAmount = pendingEarnings.reduce(
      (sum, earning) => sum + (earning.amount_earned || 0),
      0
    );

    // Create the payout record
    const { data: payout, error: payoutError } = await supabase
      .from("collaborator_payouts")
      .insert([
        {
          collaborator_id: collaboratorId,
          amount: totalAmount,
          status: "pending" as const,
        },
      ])
      .select()
      .single();

    if (payoutError) {
      console.error("Error creating payout:", payoutError);
      throw payoutError;
    }

    // Update all pending earnings to link them to this payout
    const earningIds = pendingEarnings.map((e) => e.id);
    const { error: updateError } = await supabase
      .from("collaborator_earnings")
      .update({ payout_id: payout.id })
      .in("id", earningIds);

    if (updateError) {
      console.error("Error updating earnings with payout id:", updateError);
      // If updating earnings fails, we should delete the payout to maintain consistency
      await supabase.from("collaborator_payouts").delete().eq("id", payout.id);
      throw updateError;
    }

    return {
      payout,
      earningsCount: pendingEarnings.length,
    };
  } catch (error) {
    console.error("Error creating payout request:", error);
    throw error;
  }
};

/**
 * Update payout status and handle associated earnings accordingly
 */
export const updatePayoutStatus = async (
  payoutId: number,
  status: "pending" | "completed" | "failed",
  notes?: string,
  receiptUrl?: string
): Promise<CollaboratorPayout> => {
  try {
    // Update the payout status
    interface PayoutUpdateData {
      status: "pending" | "completed" | "failed";
      payout_date?: string;
      notes?: string;
      receipt_url?: string;
    }

    const updateData: PayoutUpdateData = {
      status,
      ...(status === "completed" && { payout_date: new Date().toISOString() }),
      ...(notes && { notes }),
      ...(receiptUrl && { receipt_url: receiptUrl }),
    };

    const { data: payout, error: payoutError } = await supabase
      .from("collaborator_payouts")
      .update(updateData)
      .eq("id", payoutId)
      .select()
      .single();

    if (payoutError) {
      console.error("Error updating payout status:", payoutError);
      throw payoutError;
    }

    // Handle earnings based on status
    if (status === "failed") {
      // If payout failed, clear the payout_id from associated earnings
      const { error: clearError } = await supabase
        .from("collaborator_earnings")
        .update({ payout_id: null })
        .eq("payout_id", payoutId);

      if (clearError) {
        console.error("Error clearing payout_id from earnings:", clearError);
        throw clearError;
      }
    }
    // For "completed" and "pending" status, earnings keep their payout_id

    return payout;
  } catch (error) {
    console.error("Error updating payout status:", error);
    throw error;
  }
};

/**
 * Get pending earnings for a collaborator
 */
export const getPendingEarnings = async (
  collaboratorId: number
): Promise<CollaboratorEarning[]> => {
  const { data, error } = await supabase
    .from("collaborator_earnings")
    .select(
      `
      *,
      promotions (
        id,
        name,
        code
      ),
      user_subscriptions (
        id,
        price,
        currency_code
      )
    `
    )
    .eq("collaborator_id", collaboratorId)
    .is("payout_id", null)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching pending earnings:", error);
    throw error;
  }

  return data || [];
};

/**
 * Create a new payout for a collaborator (legacy function - kept for compatibility)
 */
export const createCollaboratorPayout = async (
  payout: Omit<CollaboratorPayout, "id" | "created_at">
): Promise<CollaboratorPayout> => {
  const { data, error } = await supabase
    .from("collaborator_payouts")
    .insert([payout])
    .select()
    .single();

  if (error) {
    console.error("Error creating collaborator payout:", error);
    throw error;
  }

  // Update the earnings to link them to this payout
  if (data) {
    // Find all pending earnings for this collaborator
    const { data: earnings, error: earningsError } = await supabase
      .from("collaborator_earnings")
      .select("id")
      .eq("collaborator_id", payout.collaborator_id)
      .is("payout_id", null);

    if (earningsError) {
      console.error("Error fetching pending earnings:", earningsError);
      throw earningsError;
    }

    // Update all pending earnings to link to this payout
    if (earnings && earnings.length > 0) {
      const earningIds = earnings.map((e) => e.id);
      const { error: updateError } = await supabase
        .from("collaborator_earnings")
        .update({ payout_id: data.id })
        .in("id", earningIds);

      if (updateError) {
        console.error("Error updating earnings with payout id:", updateError);
        throw updateError;
      }
    }
  }

  return data;
};
